import React, { useCallback, useEffect } from 'react';
import { styled } from 'styled-components';
import { Button, Popover } from 'antd';
import { useTemplateOrCalculation } from '../../../../../hooks/useTemplateOrCalculation';
import { TbAdjustmentsMinus, TbAdjustmentsPlus } from 'react-icons/tb';
import {
  ConfigurationFieldStatus,
  rtkQueryPatchStatusToConfigurationFieldStatus,
} from '@ydistri/utils';
import { createEntityListConfig, ScopeConfigurationExclusionFieldType } from './entityListLib';
import { FieldConfigurationRequest } from '@ydistri/api-sdk';
import { createPayload } from '../../../../../components/global/ConfigurationField/createPayload';
import { usePatchCategoryConfigurationMutation } from '../../../apiTemplate';
import { useDeleteEntityListConfigurationsMutation } from '../../../../../components/global/CategoryTree/apiCategoryTree';
import { ROOT_CATEGORY_ID } from '../../../../../components/global/_constants';
import { useEntityListExclusion } from './useEntityListExclusion';
import { Text } from '@ydistri/ds';

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
`;

const ButtonWrapper = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $color }) => $color && `color: ${$color};`}
  gap: 0.25rem;
`;

const IconRed = styled.span`
  color: red;
`;

const removeIcon = <TbAdjustmentsMinus />;
const redRemoveIcon = <IconRed>{removeIcon}</IconRed>;
const addIcon = <TbAdjustmentsPlus />;

interface EntityListScopeDisableButtonProps {
  categoryId: number;
  fieldType: ScopeConfigurationExclusionFieldType;
  entityListId: number;
  disabled?: boolean;
}

const EntityListScopeDisableButton: React.FC<EntityListScopeDisableButtonProps> = ({
  categoryId,
  fieldType,
  entityListId,
  disabled: disabledProp,
}) => {
  const templateOrCalculation = useTemplateOrCalculation();
  const { type } = templateOrCalculation;
  const [patchConfiguration, patchConfigurationStatus] = usePatchCategoryConfigurationMutation();
  const [deleteEntityListConfiguration, { isLoading: clearing }] =
    useDeleteEntityListConfigurationsMutation();

  const { fullExcludedConfig, isExcluded } = useEntityListExclusion(fieldType, entityListId);

  const status = rtkQueryPatchStatusToConfigurationFieldStatus(patchConfigurationStatus);

  useEffect(() => {
    patchConfigurationStatus.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want to reset mutation status after category change
  }, [categoryId]);

  const excludeEntityListHandler = useCallback(() => {
    const payload: FieldConfigurationRequest[] = createPayload(
      {
        configurationFieldType: fieldType,
        value: JSON.stringify([
          ...fullExcludedConfig.filter(c => c.EntityListId !== entityListId),
          createEntityListConfig(entityListId, 1),
        ]),
        skuType: null,
      },
      null,
    );

    patchConfiguration({
      templateId: templateOrCalculation.id,
      categoryId: ROOT_CATEGORY_ID,
      payload,
    });
  }, [entityListId, fieldType, fullExcludedConfig, patchConfiguration, templateOrCalculation.id]);

  const includeEntityListHandler = useCallback(() => {
    deleteEntityListConfiguration({
      templateId: templateOrCalculation.id,
      entityListId,
      categoryId: ROOT_CATEGORY_ID,
      configurationFieldType: fieldType,
    });
  }, [deleteEntityListConfiguration, entityListId, fieldType, templateOrCalculation.id]);

  const loading = status === ConfigurationFieldStatus.LOADING || clearing;
  const disabled = disabledProp ?? loading;
  const additionalTooltipContent = disabledProp ? ' Available only from root category.' : '';

  if (isExcluded) {
    return (
      <ButtonWrapper $color="red">
        <Text $size="small">Excluded</Text>
        <Popover
          content={`Add SKUs from this list back to the scope.${additionalTooltipContent}`}
          open={type === 'Template' ? undefined : false}
          placement="right"
        >
          <StyledButton
            className="hover-display"
            type="default"
            icon={addIcon}
            onClick={includeEntityListHandler}
            disabled={disabled}
            loading={loading}
          />
        </Popover>
        <span className="hover-hide">
          <StyledButton type="text" icon={redRemoveIcon} disabled={disabled} />
        </span>
      </ButtonWrapper>
    );
  }

  return (
    <ButtonWrapper $color="red">
      <Popover
        content={`Exclude SKUs from this list from the scope.${additionalTooltipContent}`}
        open={type === 'Template' ? undefined : false}
        placement="right"
      >
        <StyledButton
          className="hover-display"
          danger
          type="default"
          icon={removeIcon}
          onClick={excludeEntityListHandler}
          disabled={disabled}
          loading={loading}
        />
      </Popover>
    </ButtonWrapper>
  );
};

export default EntityListScopeDisableButton;
